import { Link } from "react-router-dom";
import type { GossipEvent } from "@/types/content";
import { formatHeat, getCategoryName, getCategoryPath, getEventPath } from "@/utils/content";

const statusStyle: Record<string, string> = {
  发酵中: "bg-primary-100 text-primary-700 border-primary-200",
  持续跟进: "bg-accent-100 text-accent-800 border-accent-300",
  已收尾: "bg-secondary-100 text-secondary-900 border-secondary-200",
};

interface EventCardProps {
  event: GossipEvent;
  index?: number;
}

export default function EventCard({ event, index }: EventCardProps) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link
        to={getEventPath(event.slug)}
        className="relative block h-44 w-full overflow-hidden sm:h-48"
        aria-label={event.title}
      >
        <img
          src={event.cover}
          alt={`${event.title}封面图`}
          title={`${event.title} 热搜吃瓜事件梳理`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 flex items-center gap-1 rounded-md bg-primary-600 px-2 py-1 text-xs font-medium text-background-50">
          <i className="ri-fire-fill"></i>
          {formatHeat(event.heat)}
        </span>
      </Link>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="mb-2.5 flex flex-wrap items-center gap-2">
          <Link
            to={getCategoryPath(event.categorySlug)}
            className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            {getCategoryName(event.categorySlug)}
          </Link>
          <span
            className={`rounded-md border px-2 py-0.5 text-xs font-medium ${
              statusStyle[event.status] ?? "border-background-300 bg-background-100 text-foreground-700"
            }`}
          >
            {event.status}
          </span>
          {typeof index === "number" && (
            <span className="ml-auto font-heading text-sm font-bold text-background-400">
              #{String(index + 1).padStart(2, "0")}
            </span>
          )}
        </div>

        <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
          <Link
            to={getEventPath(event.slug)}
            className="transition-colors hover:text-primary-700"
          >
            {event.title}
          </Link>
        </h3>

        <p className="mt-2.5 line-clamp-3 text-sm leading-relaxed text-foreground-600">
          {event.summary}
        </p>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {event.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-md border border-background-300 px-2 py-0.5 text-xs text-foreground-500"
            >
              #{tag}
            </span>
          ))}
        </div>

        <div className="mt-auto flex items-center justify-between border-t border-background-200 pt-3.5 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line text-base"></i>
            {event.updatedAt}
          </span>
          <Link
            to={getEventPath(event.slug)}
            className="flex items-center gap-1 whitespace-nowrap font-medium text-primary-700 transition-colors hover:text-primary-500"
          >
            看时间线
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}